import { BaseItem } from '@algolia/autocomplete-core';
import { autocomplete, AutocompleteApi } from '@algolia/autocomplete-js';
import { AutocompleteOptions } from '@algolia/autocomplete-shared';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { AccountFacade } from '@mode/accounts/contract';
import { AlgoliaSearchFacade, AlgoliaSearchHelpers, SearchSettingsModel } from '@mode/algolia/domain';
import { ErrorReporter } from '@mode/shared/contract-common';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import insightsClient, { InsightsClient } from 'search-insights';
import { isPresent } from '@mode/shared/util-js';
import { AutocompleteOptionsFactory } from './autocomplete/autocomplete-options.factory';
import { AnalyticsService } from '@mode/shared/util-segment';
import { AutocompleteFiltersWrapperElement } from './autocomplete-filters-wrapper/autocomplete-filters-wrapper.preact.component';
import { GlobalSearchService } from './global-search.service';
import { AutocompleteTagsService } from './autocomplete/autocomplete-tags.service';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';

@Component({
  selector: 'mode-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchComponent implements AfterViewInit, OnDestroy {
  public algoliaApiKey: string | undefined;

  @Input() theme: 'light' | 'dark' = 'light';
  @ViewChild('autocompleteRef', { read: ElementRef })
  public autocompleteRef: ElementRef | undefined;
  public searchSettings$: Observable<SearchSettingsModel | null> = this.algoliaSearchFacade.settings$;
  public isAnalystStudio$ = this.thoughtspotIntegrationService.currOrgIntegrated$;

  private _autocompleteApi: AutocompleteApi<BaseItem> | undefined;
  private _searchClient: SearchClient | undefined;
  protected destroyed$ = new Subject<void>();

  constructor(
    private algoliaSearchFacade: AlgoliaSearchFacade,
    private errorReporterService: ErrorReporter,
    private accountFacade: AccountFacade,
    private autocompleteTagsService: AutocompleteTagsService,
    private analyticsService: AnalyticsService,
    private globalSearchService: GlobalSearchService,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {}

  public ngAfterViewInit() {
    // Bind meta key (⌘ or ⊞) + k shortcut to open autocomplete globally
    fromEvent<KeyboardEvent>(window.document, 'keydown')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        if (event.key === 'k' && event.metaKey) {
          event.preventDefault();
          this._openAutocomplete();
        }
      });

    combineLatest([
      this.searchSettings$.pipe(filter(isPresent)),
      this.accountFacade.user$.pipe(),
      this.accountFacade.currentOrg$.pipe(),
    ]).subscribe(([settings, user, org]) => {
      this.algoliaApiKey = settings.global_search.key || '';

      if (this._autocompleteApi) {
        this.reloadAutocompleteAndInsights({ userToken: user.token, orgToken: org.token, settings });
      } else {
        this.initInsights(user.token);
        this.initAutocomplete(settings, org.token);
      }
    });
  }

  public reloadAutocompleteAndInsights({
    orgToken,
    userToken,
    settings,
  }: {
    orgToken: string;
    userToken: string | undefined;
    settings: SearchSettingsModel;
  }) {
    // re-init insights client
    this.initInsights(userToken);
    // re-init search client
    this.initSearchClient();
    // reset filters ref
    this.globalSearchService.updateFiltersWrapperRef(null, true);
    // re-init autocomplete options and update autocomplete to reload search client renderer
    this._autocompleteApi?.update(this._getAutocompleteOptions(settings, orgToken));
  }

  public initInsights(userToken: string | undefined) {
    const appId = this.algoliaSearchFacade.algoliaAppId ?? '';
    const apiKey = this.algoliaApiKey;

    if (userToken) {
      this._insightsClient('init', { appId, apiKey });
      this._insightsClient('setUserToken', userToken); // it's recommended to set the user token with our own token
    } else {
      // user has not logged in probably so use an anonymous id
      this._insightsClient('init', { appId, apiKey, useCookie: true });
    }
  }

  public initAutocomplete(settings: SearchSettingsModel, orgToken: string) {
    this.initSearchClient();

    this._autocompleteApi = this._autocomplete(this._getAutocompleteOptions(settings, orgToken));
  }

  public initSearchClient() {
    const appId = this.algoliaSearchFacade.algoliaAppId;
    const apiKey = this.algoliaApiKey ?? '';
    const searchClient = this._algoliasearch(appId, apiKey);

    this._searchClient = AlgoliaSearchHelpers.hackMoveSearchClientApiKeyFromHeaderToRequestBody(searchClient, apiKey);
  }

  /**
   * Pass through to insights client singleton helper
   */
  public _insightsClient(...args: Parameters<InsightsClient>): ReturnType<InsightsClient> {
    // eslint-disable-next-line prefer-spread
    return insightsClient.apply(null, args);
  }

  /**
   * Pass through to autocomplete client helper
   */
  public _autocomplete(...args: Parameters<typeof autocomplete>): ReturnType<typeof autocomplete> {
    return autocomplete(...args);
  }

  /**
   * Pass through to algoliasearch client helper
   */
  public _algoliasearch(...args: Parameters<typeof algoliasearch>): ReturnType<typeof algoliasearch> {
    return algoliasearch(...args);
  }

  /**
   * Open autocomplete helper
   */
  public _openAutocomplete() {
    this._autocompleteApi?.setIsOpen(true);
  }

  public _getAutocompleteOptions(searchSettings: SearchSettingsModel, orgToken: string): AutocompleteOptions<BaseItem> {
    this.autocompleteTagsService.resetTagsPlugin();

    return new AutocompleteOptionsFactory(
      this.globalSearchService.store.vm$,
      this.errorReporterService,
      this.autocompleteTagsService,
      this.algoliaSearchFacade.algoliaAppId,
      this.algoliaApiKey ?? '',
      this.analyticsService,
      (elem: AutocompleteFiltersWrapperElement) => this.globalSearchService.updateFiltersWrapperRef(elem),
      orgToken
    ).createOptions({
      container: this.autocompleteRef?.nativeElement,
      searchClient: this._searchClient as SearchClient,
      reportIndexName: searchSettings.report_relevance.indexName,
      collectionIndexName: searchSettings.collection_relevance.indexName,
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
